import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAutomations } from "../hooks/useApi";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import ErrorDisplay from "../components/ui/ErrorDisplay";
import { StatusBadge } from "../components/ui/StatusIndicators";
import AutomationForm from "../components/forms/AutomationForm";
import {
  TrashIcon,
  PencilIcon,
  EyeIcon,
  HomeIcon,
  ChevronRightIcon,
  PlayIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  CommandLineIcon,
} from "@heroicons/react/24/outline";
import { deleteAutomation, triggerAutomation } from "../services/api";

function AutomationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [automation, setAutomation] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTriggering, setIsTriggering] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mock API call for single automation - normally would come from useAutomations
  useEffect(() => {
    const fetchAutomation = async () => {
      try {
        setLoading(true);
        // This is a mock - normally you'd have an API endpoint for single automation
        // For now, we'll simulate loading a single automation

        // Mock data for the automation
        const mockAutomation = {
          id: id,
          name: `Automation ${id}`,
          description:
            "This is a sample automation description that explains what this automation does and how it helps streamline processes.",
          type: "workflow",
          status: "active",
          totalRuns: 245,
          failedRuns: 12,
          inProgress: 3,
          metrics: {
            successRate: 95.1,
            averageExecutionTime: "2.3 seconds",
            lastRun: "2024-01-15T10:30:00Z",
            lastSuccess: "2024-01-15T10:30:00Z",
          },
          trigger: {
            type: "schedule",
            schedule: "0 9 * * 1-5", // Every weekday at 9 AM
            enabled: true,
          },
          actions: [
            {
              type: "email",
              name: "Send notification",
              config: {
                to: "admin@company.com",
                subject: "Daily Report",
                template: "daily_report",
              },
            },
            {
              type: "data_processing",
              name: "Update database",
              config: {
                table: "daily_stats",
                operation: "insert",
              },
            },
          ],
          createdAt: "2024-01-01T12:00:00Z",
          updatedAt: "2024-01-15T08:00:00Z",
        };

        setAutomation(mockAutomation);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchAutomation();
    }
  }, [id]);

  const handleSave = async (automationData) => {
    try {
      // TODO: Implement actual API call to update automation
      console.log("Saving automation:", automationData);
      setIsEditing(false);
      // Update local state with new data
      setAutomation({ ...automation, ...automationData });
    } catch (error) {
      console.error("Failed to save automation:", error);
      alert("Failed to save automation. Please try again.");
    }
  };

  const handleDelete = async () => {
    if (
      window.confirm(
        `Are you sure you want to delete "${automation.name}"? This action cannot be undone.`
      )
    ) {
      setIsDeleting(true);
      try {
        await deleteAutomation(id);
        navigate("/automations");
      } catch (error) {
        console.error("Failed to delete automation:", error);
        alert("Failed to delete automation. Please try again.");
        setIsDeleting(false);
      }
    }
  };

  const handleTrigger = async () => {
    setIsTriggering(true);
    try {
      await triggerAutomation(id);
      alert(`Successfully triggered "${automation.name}"!`);
      // Refresh automation data to show updated run counts
      window.location.reload();
    } catch (error) {
      alert(`Failed to trigger automation: ${error.message}`);
    } finally {
      setIsTriggering(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-green-100 text-green-800",
      paused: "bg-yellow-100 text-yellow-800",
      inactive: "bg-gray-100 text-gray-800",
      error: "bg-red-100 text-red-800",
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  const getTypeLabel = (type) => {
    const types = {
      api: "API Integration",
      email: "Email",
      document: "Document",
      data_processing: "Data Processing",
      notification: "Notification",
      workflow: "Workflow",
    };
    return types[type] || type;
  };

  const formatPercentage = (rate) => {
    if (typeof rate !== "number") return "N/A";
    return `${rate}%`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString();
  };

  // Handle loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="large" message="Loading automation..." />
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="py-12">
        <ErrorDisplay
          error={error}
          message="Failed to load automation"
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  // Handle automation not found
  if (!automation) {
    return (
      <div className="py-12">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Automation Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The automation with ID "{id}" could not be found.
          </p>
          <Link to="/automations" className="btn-primary">
            ← Back to Automations
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-2 text-sm text-gray-500">
          <li>
            <Link to="/" className="hover:text-gray-700">
              <HomeIcon className="w-4 h-4" />
            </Link>
          </li>
          <ChevronRightIcon className="w-4 h-4" />
          <li>
            <Link to="/automations" className="hover:text-gray-700">
              Automations
            </Link>
          </li>
          <ChevronRightIcon className="w-4 h-4" />
          <li className="text-gray-900 font-medium truncate">
            {automation.name}
          </li>
        </ol>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="text-3xl font-bold text-gray-900 truncate">
            {automation.name}
          </h1>
          <div className="mt-2 flex items-center space-x-4">
            <StatusBadge status={automation.status} />
            <span className="text-sm text-gray-500">
              {getTypeLabel(automation.type)}
            </span>
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          {!isEditing ? (
            <>
              {automation.status === "active" && (
                <button
                  onClick={handleTrigger}
                  disabled={isTriggering}
                  className="btn-secondary"
                >
                  <PlayIcon className="w-4 h-4 mr-2" />
                  {isTriggering ? "Triggering..." : "Trigger"}
                </button>
              )}
              <button
                onClick={() => setIsEditing(true)}
                className="btn-secondary"
              >
                <PencilIcon className="w-4 h-4 mr-2" />
                Edit
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="btn-danger"
              >
                <TrashIcon className="w-4 h-4 mr-2" />
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(false)}
              className="btn-secondary"
            >
              <EyeIcon className="w-4 h-4 mr-2" />
              View Mode
            </button>
          )}
        </div>
      </div>

      {/* Metrics Cards */}
      {!isEditing && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="card">
            <div className="flex items-center">
              <ChartBarIcon className="w-8 h-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Runs</p>
                <p className="text-lg font-semibold text-gray-900">
                  {automation.totalRuns || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <CheckCircleIcon className="w-8 h-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">
                  Success Rate
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatPercentage(automation.metrics?.successRate)}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <ClockIcon className="w-8 h-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Avg Time</p>
                <p className="text-lg font-semibold text-gray-900">
                  {automation.metrics?.averageExecutionTime || "N/A"}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <ExclamationCircleIcon className="w-8 h-8 text-red-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Failed Runs</p>
                <p className="text-lg font-semibold text-gray-900">
                  {automation.failedRuns || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Automation Details */}
      {!isEditing && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Basic Info */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Basic Information
            </h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Description
                </dt>
                <dd className="text-sm text-gray-900">
                  {automation.description}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Type</dt>
                <dd className="text-sm text-gray-900">
                  {getTypeLabel(automation.type)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(automation.status)}`}
                  >
                    {automation.status?.charAt(0).toUpperCase() +
                      automation.status?.slice(1)}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="text-sm text-gray-900">
                  {formatDate(automation.createdAt)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Last Updated
                </dt>
                <dd className="text-sm text-gray-900">
                  {formatDate(automation.updatedAt)}
                </dd>
              </div>
            </dl>
          </div>

          {/* Execution History */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Execution History
            </h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Total Executions
                </dt>
                <dd className="text-sm text-gray-900">
                  {automation.totalRuns || 0}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Failed Executions
                </dt>
                <dd className="text-sm text-gray-900">
                  {automation.failedRuns || 0}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Currently Running
                </dt>
                <dd className="text-sm text-gray-900">
                  {automation.inProgress || 0}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Last Run</dt>
                <dd className="text-sm text-gray-900">
                  {formatDate(automation.metrics?.lastRun)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Last Success
                </dt>
                <dd className="text-sm text-gray-900">
                  {formatDate(automation.metrics?.lastSuccess)}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}

      {/* Trigger Configuration */}
      {!isEditing && automation.trigger && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Trigger Configuration
          </h3>
          <dl className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Trigger Type
              </dt>
              <dd className="text-sm text-gray-900 capitalize">
                {automation.trigger.type}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Schedule</dt>
              <dd className="text-sm text-gray-900 font-mono">
                {automation.trigger.schedule || "N/A"}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Enabled</dt>
              <dd>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    automation.trigger.enabled
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {automation.trigger.enabled ? "Yes" : "No"}
                </span>
              </dd>
            </div>
          </dl>
        </div>
      )}

      {/* Actions */}
      {!isEditing && automation.actions && automation.actions.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Actions ({automation.actions.length})
          </h3>
          <div className="space-y-3">
            {automation.actions.map((action, index) => (
              <div
                key={index}
                className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
              >
                <CommandLineIcon className="w-5 h-5 text-indigo-600 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {action.name}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">
                    {action.type.replace("_", " ")}
                  </p>
                  {action.config && Object.keys(action.config).length > 0 && (
                    <div className="mt-2">
                      <details className="text-xs">
                        <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
                          View Configuration
                        </summary>
                        <pre className="mt-2 p-2 bg-white rounded border text-xs overflow-x-auto">
                          {JSON.stringify(action.config, null, 2)}
                        </pre>
                      </details>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form */}
      {isEditing && (
        <div className="card">
          <AutomationForm
            isOpen={isEditing}
            onClose={() => setIsEditing(false)}
            onSave={handleSave}
            automation={automation}
            templates={[]} // You might want to pass actual templates here
            loading={false}
          />
        </div>
      )}
    </div>
  );
}

export default AutomationDetail;
